import { t } from '@/plugins/i18n';
import { PlanComparison } from '@/datas/plan-comparison';
import { getBizLicenseCount, getEnterpriseLicenseCount, getProLicenseCount } from '@/datas/comparison-tools';
import { showDialog } from '@/utils/export';

export class WorkplaceComparison extends PlanComparison {
  constructor(props = {}) {
    super(props);
  }

  initTable() {
    this.tableData = getTableData(this.props);
    return this;
  }

  filterBy() {
    const tableData = this.tableData;
    this.tableData.coreFeatures = tableData.coreFeatures.filter(item => {
      return item.shouldHide !== true;
    });
    this.tableData.meetings = tableData.meetings.filter(item => {
      return item.shouldHide !== true;
    });
    this.tableData.security = tableData.security.filter(item => {
      return item.shouldHide !== true;
    });
    this.tableData.phone = tableData.phone.filter(item => {
      return item.shouldHide !== true;
    });
    return this;
  }

  buildTree() {
    const data = this.tableData;
    this.treeData = [
      {
        feature: t('fbc.common.core_features'),
        id: ComparisonTypeMap.CORE_FEATURES,
        subTitle: true,
        children: mapItemKey(
          data.coreFeatures || [],
          ComparisonTypeMap.CORE_FEATURES,
        ),
      },
      {
        feature: 'Meetings',
        id: ComparisonTypeMap.MEETINGS,
        subTitle: true,
        children: mapItemKey(
          data.meetings || [],
          ComparisonTypeMap.MEETINGS,
        ),
      },
      {
        feature: 'Team Chat',
        id: ComparisonTypeMap.TEAM_CHAT,
        subTitle: true,
        children: mapItemKey(
          data.teamChat || [],
          ComparisonTypeMap.TEAM_CHAT,
        ),
      },
      {
        feature: 'Integrations',
        id: ComparisonTypeMap.INTEGRATIONS,
        subTitle: true,
        children: mapItemKey(
          data.integrations || [],
          ComparisonTypeMap.INTEGRATIONS,
        ),
      },
      {
        feature: 'Engagement',
        id: ComparisonTypeMap.FUN,
        subTitle: true,
        children: mapItemKey(data.fun || [], ComparisonTypeMap.FUN),
      },
      {
        feature: 'Security and Compliance',
        id: ComparisonTypeMap.SECURITY,
        subTitle: true,
        children: mapItemKey(
          data.security || [],
          ComparisonTypeMap.SECURITY,
        ),
      },
      ...(this.props?.showAdvancedSecurity ? [{
        feature: 'Advanced Security Features',
        id: ComparisonTypeMap.ADVANCED_SECURITY,
        subTitle: true,
        children: mapItemKey(
          [...data.advancedSecurity] || [],
          ComparisonTypeMap.ADVANCED_SECURITY,
        ),
      }] : []),
      ...(this.props?.hidePhone ? [] : [{
        feature: 'Phone',
        id: ComparisonTypeMap.PHONE,
        subTitle: true,
        children: mapItemKey(
          [...data.phone] || [],
          ComparisonTypeMap.PHONE,
        ),
      }]),
      {
        feature: t('fbc.common.support'),
        id: ComparisonTypeMap.SUPPORT,
        subTitle: true,
        children: mapItemKey(
          data.support || [],
          ComparisonTypeMap.SUPPORT,
        ),
      },
    ];
    return this;
  }

}

const ComparisonTypeMap = {
  PHONE: 'phone',
  CORE_FEATURES: 'coreFeatures',
  MEETINGS: 'meetings',
  TEAM_CHAT: 'teamChat',
  INTEGRATIONS: 'integrations',
  FUN: 'fun',
  SECURITY: 'security',
  SUPPORT: 'support',
  ADVANCED_SECURITY: 'advancedSecurity'
};
const mapItemKey = (data, pid) =>
  data.map((item, idx) => ({
    ...item,
    id: `${pid}_${idx}`,
  }));

const getParticipantCapacityExtraLink = (plan, largeMeetingConfig = {}) => {
  const config = largeMeetingConfig?.[plan] || {}
  return {
    text: !config.display ? '' : config.disabled ?
      t('common.participant_limit.expandable_with_large_meetings') :
      t('common.participant_limit.increase_with_large_meetings'),
    disabled: config.disabled,
    callback: config.callback,
  }
}

const getTableData = (param) => {
  const {
    unlockProLicenseCount, unlockBizLicenseCount, unlockBizMaxLicenseCount, EssentialAPPCallback,
    AICompanionCallBack, VisitorManagementCallback, currentCountry, freeZoomClipsQuantity, enableDocsFeature, showDocsLearnMore, DocsCallBack, largeMeetingConfig, enableDisplayZoomTasksWithoutAccountCheck, showTasksLearnMore, TasksCallback, isUsCountry, showCustomAvatarLearnMore, isEligibleAccount4CustomAvatarFeature,
    CustomAvatarCallback, enableDisplayZoomWorkFlow
  } = param;
  return {
    coreFeatures: [
      {
        feature: t('meetings.fullPlanItem2'),
        tooltip: t('meetings.fullPlanItem2Tip'),
        isString: true,
        basic: '1',
        basicPlus: '1',
        pro: getProLicenseCount(unlockProLicenseCount),
        proPlus: '1-99',
        business: getBizLicenseCount(unlockBizLicenseCount, unlockBizMaxLicenseCount),
        businessPlus: getBizLicenseCount(unlockBizLicenseCount, unlockBizMaxLicenseCount),
        enterprise: getEnterpriseLicenseCount(unlockBizMaxLicenseCount),
      },
      {
        feature: t('phone.fullPlanItem30'),
        tooltip:
          t('simplifiedVideoConferencingAndMessagingAcrossAnyDevice'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem5'),
        tooltip: t('meetings.fullPlanItem5Tip'),
        isString: true,
        basic: t('meetings.fullPlanItem5_basic'),
        basicPlus: t('one.basicPlusFullPlanItem1'),
        pro: t('meetings.fullPlanItem5_pro'),
        proPlus: t('meetings.fullPlanItem5_pro'),
        business: t('meetings.fullPlanItem5_biz'),
        businessPlus: t('meetings.fullPlanItem5_biz'),
        enterprise: t('meetings.fullPlanItem5_ent'),
      },
      {
        feature: t('meetings.fullPlanItem1'),
        tooltip: t('meetings.fullPlanItem1Tip'),
        isString: true,
        basic: 100,
        basicPlus: 100,
        pro: 100,
        proPlus: 100,
        business: 300,
        businessPlus: 300,
        enterprise: t('meetings.fullPlanItem1_ent'),
        extraLink: {
          pro: getParticipantCapacityExtraLink('pro', largeMeetingConfig),
          proPlus: getParticipantCapacityExtraLink('proPlus', largeMeetingConfig),
          business: getParticipantCapacityExtraLink('business', largeMeetingConfig),
          businessPlus: getParticipantCapacityExtraLink('businessPlus', largeMeetingConfig)
        }
      },
      {
        feature: t('meetings.fullPlanItem7'),
        tooltip: t('meetings.fullPlanItem7Tip'),
        isString: true,
        basic: t('meetings.fullPlanItem7_basic'),
        basicPlus: t('meetings.fullPlanItem7_basic'),
        pro: t('meetings.fullPlanItem7_pro'),
        proPlus: t('meetings.fullPlanItem7_pro'),
        business: t('meetings.fullPlanItem7_pro'),
        businessPlus: t('meetings.fullPlanItem7_biz'),
        enterprise: t('meetings.fullPlanItem7_ent'),
      },
      {
        feature: t('one.messaging'),
        tooltip: t('meetings.fullPlanItem16Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem51'),
        isString: true,
        basic: '3',
        basicPlus: '3',
        pro: '3',
        proPlus: '3',
        business: t('meetings.unlimited'),
        businessPlus: t('meetings.unlimited'),
        enterprise: t('meetings.unlimited'),
      },
      {
        shouldHide: !enableDisplayZoomWorkFlow,
        feature: t('billing.plan.feature.workflow.title'),
        tooltip: t('billing.plan.feature.workflow.tip'),
        isString: true,
        basic: false,
        isNew: true,
        basicPlus: t('billing.plan.feature.workflow.label'),
        pro: t('billing.plan.feature.workflow.label'),
        proPlus: t('billing.plan.feature.workflow.label'),
        business: t('billing.plan.feature.workflow.label'),
        businessPlus: t('billing.plan.feature.workflow.label'),
        enterprise: t('billing.plan.feature.workflow.label'),
        link: {
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Zoom WorkFlow Automation',
          callback: async ()=> showDialog(await import(/* webpackChunkName: "zoom_work_flow_automation_dialog" */ '@/components/ZOOM_WORK_FLOW_AUTOMATION_DIALOG'), { visible: true })
        },
      },
      {
        feature: t('meetings.fullPlanItem_EssentialApp'),
        isNew: false,
        link: {
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Essential Apps',
          async callback() {
            await EssentialAPPCallback && EssentialAPPCallback();
          }
        },
        isString: true,
        basic: false,
        basicPlus: false,
        pro: t('meetings.fullPlanItem_EssentialAppText'),
        proPlus: t('meetings.fullPlanItem_EssentialAppText'),
        business: t('meetings.fullPlanItem_EssentialAppText'),
        businessPlus: t('meetings.fullPlanItem_EssentialAppText'),
        enterprise: false,
      },
      {
        feature: t('mail'),
        tooltip: t('zoomMailAndCalendarInTheZoomApp'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        shouldHide: !['US', 'CA'].includes(currentCountry),
        feature: t('mailService'),
        tooltip: t('emailServiceWithFullTimeEndToEnd'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('customMailDomain'),
        tooltip: t('customMailDomainWithMailService'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('calendarClient'),
        tooltip: t('scheduleAndJoinMeetings'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('aiCompanion'),
        tooltip: '',
        business: true,
        businessPlus: true,
        enterprise: true,
        pro: true,
        proPlus: true,
        link: {
          classname: 'open-share-modal',
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about AI Companion',
          async callback() {
            await AICompanionCallBack && AICompanionCallBack();
          }
        },
      },
      {
        shouldHide: !['US', 'CA'].includes(currentCountry),
        feature: t('calendarService'),
        tooltip: t('meetingSchedulingAndAppointment'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('marketing.new_pricing.clips'),
        isString: true,
        tooltip: t('allPlansClipsCoreFeatureToolTip'),
        basic: `${freeZoomClipsQuantity} ${t('included')}`,
        pro: t('meetings.unlimited'),
        proPlus: t('meetings.unlimited'),
        business: t('meetings.unlimited'),
        businessPlus: t('meetings.unlimited'),
        enterprise: t('meetings.unlimited'),
      },
      {
        feature: t('fbc.plan_details.custom_avatar.title'),
        tooltip: t('fbc.plan_details.custom_avatar.comparison_table_tips'),
        shouldHide: !isEligibleAccount4CustomAvatarFeature,
        isString: true,
        isNew: true,
        basic: t('fbc.plan_details.custom_avatar.basic_feature'),
        business: t('fbc.plan_details.custom_avatar.pricing_feature'),
        businessPlus: t('fbc.plan_details.custom_avatar.pricing_feature'),
        enterprise: t('fbc.plan_details.custom_avatar.pricing_feature'),
        pro: t('fbc.plan_details.custom_avatar.pricing_feature'),
        proPlus: t('fbc.plan_details.custom_avatar.pricing_feature'),
        link: showCustomAvatarLearnMore ? {
          classname: 'manual-open-custom-avatar-dialog',
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Custom Avatar',
          async callback() {
            await CustomAvatarCallback && CustomAvatarCallback();
          }
        }: null
      },
      {
        feature: t('marketing.new_pricing.notes'),
        tooltip: t('allPlansNotesCoreFeatureToolTip'),
        basic: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('common.phoneTab'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
        enterpriseDesc: t('enterprise_essentials_zp_not_available'),
      },
      {
        feature: t('fbc.plan_details.biz_plus.docs.docs'),
        tooltip: t('fbc.plan_details.biz_plus.docs.comparison_table_tips'),
        shouldHide: !enableDocsFeature,
        isString: true,
        isNew: false,
        basic: t('fbc.plan_details.limited_features'),
        business: true,
        businessPlus: true,
        enterprise: true,
        pro: true,
        proPlus: true,
        link: showDocsLearnMore ? {
          classname: 'manual-open-docs-dialog',
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Zoom Docs',
          async callback() {
            await DocsCallBack && DocsCallBack();
          }
        } : null
      },
      {
        feature: t('fbc.plan_details.biz_plus.tasks.tasks'),
        tooltip: t('fbc.plan_details.biz_plus.tasks.comparison_table_tips'),
        shouldHide: !enableDisplayZoomTasksWithoutAccountCheck,
        isString: true,
        isNew: true,
        basic: t('fbc.plan_details.limited_features'),
        business: true,
        businessPlus: true,
        enterprise: true,
        pro: true,
        proPlus: true,
        link: showTasksLearnMore ? {
          classname: 'manual-open-tasks-dialog',
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Zoom Tasks',
          async callback() {
            await TasksCallback && TasksCallback();
          }
        } : null
      },
      {
        feature: t('automatedMeetingsCaptions'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('translatedCaptions-0'),
        businessPlus: true,
        enterprise: true,
      },
      {
        shouldHide: isUsCountry,
        feature: t('workspaceReservation'),
        isString: true,
        businessPlus: true,
        enterprise: '*Included in Enterprise Plus',
      },
      {
        feature: t('billing.visitor_management.title'),
        isNew: true,
        businessPlus: true,
        link: {
          classname: 'visitorDialogLink open-visitor-dialog',
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Visitor Management',
          async callback() {
            await VisitorManagementCallback && VisitorManagementCallback();
          }
        },
      },
      {
        feature: 'Zoom Scheduler',
        isString: true,
        tooltip: 'Zoom Scheduler optimizes scheduling by sharing your availability for others to conveniently book appointments. Works seamlessly with your Zoom Meetings and Zoom Mail and Calendar, and integrates with Google and Microsoft 365.',
        business: true,
        businessPlus: true,
        enterprise: '*Included in Enterprise Plus',
      },
      {
        feature: t('accessAcrossAnyDevice'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    meetings: [
      {
        feature: t('meetings.fullPlanItem1'),
        tooltip: t('meetings.fullPlanItem1Tip'),
        isString: true,
        basic: 100,
        basicPlus: 100,
        pro: 100,
        proPlus: 100,
        business: 300,
        businessPlus: 300,
        enterprise: t('meetings.fullPlanItem1_ent'),
      },
      {
        feature: t('meetings.fullPlanItem4'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem5'),
        tooltip: t('meetings.fullPlanItem5Tip'),
        isString: true,
        basic: t('meetings.fullPlanItem5_basic'),
        basicPlus: t('one.basicPlusFullPlanItem1'),
        pro: t('meetings.fullPlanItem5_pro'),
        proPlus: t('meetings.fullPlanItem5_pro'),
        business: t('meetings.fullPlanItem5_biz'),
        businessPlus: t('meetings.fullPlanItem5_biz'),
        enterprise: t('meetings.fullPlanItem5_ent'),
      },
      {
        feature: t('meetings.fullPlanItem7'),
        tooltip: t('meetings.fullPlanItem7Tip'),
        isString: true,
        basic: t('meetings.fullPlanItem7_basic'),
        basicPlus: t('meetings.fullPlanItem7_basic'),
        pro: t('meetings.fullPlanItem7_pro'),
        proPlus: t('meetings.fullPlanItem7_pro'),
        business: t('meetings.fullPlanItem7_pro'),
        businessPlus: t('meetings.fullPlanItem7_biz'),
        enterprise: t('meetings.fullPlanItem7_ent'),
      },
      {
        feature: t('meetings.fullPlanItem9'),
        tooltip: t('meetings.fullPlanItem9Tip'),
        isString: true,
        pro: t('meetings.fullPlanItem9_pro'),
        proPlus: t('meetings.fullPlanItem9_pro'),
        business: t('meetings.fullPlanItem9_biz'),
        businessPlus: t('meetings.fullPlanItem9_biz'),
        enterprise: t('meetings.fullPlanItem9_ent'),
      },
      {
        feature: t('meetings.fullPlanItem_EssentialApp'),
        isNew: false,
        link: {
          text: t('common.learnMore'),
          style: {
            fontSize: '12px',
            fontWeight: 700,
            color: '#0B5CFF',
          },
          ariaLabel: 'Learn more about Essential Apps',
          async callback() {
            await EssentialAPPCallback && EssentialAPPCallback();
          }
        },
        isString: true,
        basic: false,
        basicPlus: false,
        pro: t('meetings.fullPlanItem_EssentialAppText'),
        proPlus: t('meetings.fullPlanItem_EssentialAppText'),
        business: t('meetings.fullPlanItem_EssentialAppText'),
        businessPlus: t('meetings.fullPlanItem_EssentialAppText'),
        enterprise: false,
      },
      {
        feature: t('meetings.fullPlanItem11'),
        tooltip: t('meetings.fullPlanItem11Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem12'),
        tooltip: t('meetings.fullPlanItem12Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem14'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem15'),
        tooltip: t('meetings.fullPlanItem15Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem17'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem18'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem19'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem21'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem29'),
        tooltip: t('meetings.fullPlanItem29Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem41'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem61'),
        tooltip: t('meetings.fullPlanItem61Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem62'),
        tooltip: t('meetings.fullPlanItem62Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanNewItem1'),
        tooltip: t('meetings.fullPlanNewItem1Tip'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanNewItem2'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanNewItem3'),
        tooltip: t('meetings.fullPlanNewItem3Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem24'),
        tooltip: t('meetings.fullPlanItem24Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem25'),
        tooltip: t('meetings.fullPlanItem25Tip'),
        isString: true,
        pro: t('meetings.fullPlanItem25_pro'),
        proPlus: t('meetings.fullPlanItem25_pro'),
        business: t('meetings.fullPlanItem25_pro'),
        businessPlus: t('meetings.fullPlanItem25_biz'),
        enterprise: t('meetings.fullPlanItem25_biz'),
      },
      {
        feature: t('meetings.fullPlanItem26'),
        tooltip: t('meetings.fullPlanItem26Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem27'),
        tooltip: t('meetings.fullPlanItem27Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem28'),
        tooltip: t('meetings.fullPlanItem28Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem30'),
        tooltip: t('meetings.fullPlanItem30Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('one.autoCaptions'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
        desc: t('multiLanguage'),
      },
      {
        feature: t('meetings.fullPlanItem31'),
        tooltip: t('meetings.fullPlanItem31Tip'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem32'),
        tooltip: t('meetings.fullPlanItem32Tip'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem33'),
        tooltip: t('meetings.fullPlanItem33Tip'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem34'),
        tooltip: t('meetings.fullPlanItem34Tip'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem35'),
        tooltip: t('meetings.fullPlanItem35Tip'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetings.fullPlanItem37'),
        tooltip: t('meetings.fullPlanItem37Tip'),
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        shouldHide: isUsCountry,
        feature: t('meetings.fullPlanItem39'),
        tooltip: t('meetings.fullPlanItem39Tip'),
        enterprise: true,
      },
      {
        shouldHide: isUsCountry,
        feature: t('meetings.fullPlanNewItem4'),
        enterprise: true,
        enterpriseDesc: t('enterprisePlusIncludesZoomSessions_1000'),
      },
      {
        feature: t('one.translatedCations'),
        businessPlus: true,
        enterprise: t('one.entPlus'),
      },
      {
        feature: t('customMailDomain'),
        tooltip: t('customMailDomainWithMailService'),
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    teamChat: [
      {
        feature: t('directChannelOrGroupChat'),
        tooltip:
          t('easilyCommunicateIn_1_1MessagesSmallGroupsOrWithLargeTeams'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('messageAndFileHistory'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('findPeopeChannelsAndFilesInSearch'),
        tooltip:
          t('quicklyFindContactsInstantMessagesGroupChatsAndFilesKeepingKnowledgeAndInformationAtYourFingertips'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('applicationIntegrations'),
        tooltip:
          t('boostProductivityByLeveragingYourFavoriteAppsRightWithinTeamChat'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('secureFileSharing'),
        tooltip: t('enhanceCollaborationWithSeamlessFileSharing'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('persistentMeetingChat'),
        tooltip:
          t('neverLoseTrackOfMeetingConversationsCommunicateBeforeDuringAndAfterAMeetingInADedicatedGroupChatInZoomTeamChatForAllMeetingParticipants'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('cloudStorage'),
        tooltip:
          t('backChannelsToTheZoomCloudOrAThirdPartyStorageProvider'),
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    integrations: [
      {
        feature: 'Microsoft',
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: 'Google',
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: 'Salesforce',
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: 'ServiceNow',
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: 'Hubspot',
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    fun: [
      {
        feature: t('avatars'),
        tooltip:
          t('customizeYourOwnAvatarToBringSomeFunIntoMeetingsAndPresentYourselfDynamicallyWithoutNeedingToBeOnVideo'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('inMeetingReactions'),
        tooltip:
          t('showEngagementAndQuicklyCommunicateWithOtherParticipantsInANonVerbalWay'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('virtualBackgrounds'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('videoFilters'),
        tooltip:
          t('injectSomeFunIntoYourMeetingsWithAVarietyOfFiltersOverlayedOntoYourVideo'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('inMeetingPollsAndQuizzes'),
        tooltip:
          t('createSingleChoiceOrMultipleChoicePollingQuestionsForYourMeetings'),
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('liveStreamingAcrossPlatforms'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('appearanceTouchUp'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('originalSoundForMusicians'),
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    security: [
      {
        feature: t('endToEndEncryptionForZoomMeetings'),
        tooltip: t(
          'whenEnabledZoomsEndToEndEncryptionE2eeForZoomMeetings',
        ),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        shouldHide: isUsCountry,
        feature: t('zoomCustomerManagedKey'),
        tooltip: t('bringYourOwnEncryptionKeysToProtectZoomDataAtRest'),
        enterprise: t('one.entPlus'),
      },
      {
        feature: t('enterpriseGradeSecurityFeatures'),
        tooltip: t('256BitAesGcmEncryptionForRealTimeContent'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('marketing.plan_comparison.security.enc_by_default'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('marketing.plan_comparison.security.chat_etiquette'),
        basic: false,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('marketing.plan_comparison.security.gdpr_control'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem40'),
        tooltip: t('phone.fullPlanItem40Tip'),
        proPlus: false,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
    ],
    advancedSecurity: [
      {
        feature: t('archiving_api'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('dlp_api'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('info_barriers'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('chat_etiquette'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('gdrp'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('regiona_data_centers'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('zoom_compliance_manager'),
        isString: true,
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: t('paid_option'),
      },
      {
        feature: t('encryption_by_default'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('end_to_end_encryption'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('cryptographic_boundary'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('vdi'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('customer_managed_key'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('gpo_msi_policies'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('msi'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('ntt'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('dashboards'),
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: true,
      },
      {
        feature: t('zoom_mesh'),
        isString: true,
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: t('paid_option'),
      },
      {
        feature: t('zoom_phone_survivability'),
        isString: true,
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: t('paid_option'),
      },
      {
        feature: t('zoom_meeting_survivability'),
        isString: true,
        basic: false,
        basicPlus: false,
        pro: false,
        proPlus: false,
        business: false,
        businessPlus: false,
        enterprise: t('paid_option'),
      },
    ],
    phone: [
      {
        feature: t('phone.fullPlanItem1'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem2'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem3'),
        proPlus: true,
        businessPlus: true,
      },
      {
        feature: t('phone.fullPlanItem4'),
        tooltip: t('phone.fullPlanItem4Tip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem5'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem6'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem7'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem8'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem9'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem10'),
        tooltip: t('phone.fullPlanItem10Tip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem11'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem12'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem13'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem14'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem15'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem16'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem17'),
        tooltip: t('phone.fullPlanItem17Tip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem18'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem19'),
        tooltip: t('phone.fullPlanItem19Tip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem20'),
        tooltip: t('phone.fullPlanItem20Tip'),
        pdf: true,
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem21'),
        tooltip: t('phone.fullPlanItem21Tip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem22'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem23'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem24'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('endToEndEncryptionForZoomPhone'),
        tooltip: t('whenEnabledForZoomPhoneEndToEndEncryptionE2ee'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem43'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem44'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem46'),
        proPlus: false,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem47'),
        tooltip: t('phone.fullPlanItem47Tip'),
        proPlus: false,
        businessPlus: true,
        enterprise: true,
      },
      {
        isString: true,
        feature: t('billing.pbx.phone_support_24_7_live_chat'),
        tooltip: t('availableToAllPhoneStandaloneCustomersSupportPlansWillVaryWhenPairedWithMeetingsPlans'),
        proPlus: t('billing.zone_pro_plus.pro_plus_phone_features'),
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem49'),
        tooltip: t('phone.fullPlanItem49Tip'),
        proPlus: false,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem50'),
        proPlus: false,
        businessPlus: true,
        enterprise: true,
      },
      {
        suppurtCountry: 'us',
        feature: t('phone.fullPlanSms'),
        tooltip: t('phone.fullPlanSmsTip'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('phone.fullPlanItem51'),
        proPlus: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        shouldHide: !['US', 'CA'].includes(currentCountry),
        feature: t('phone.fullPlanItem61'),
        tooltip: t('phone.fullPlanItem61Tip', { href: '/billing/pbx/rates' }),
        pro: false,
        proPlus: true,
        business: false,
        businessPlus: true,
      }
    ],
    support: [
      {
        feature: t('meetings.fullPlanItem6'),
        tooltip: t('meetings.fullPlanItem6Tip'),
        isString: true,
        basic: t('meetings.fullPlanItem6_basic'),
        basicPlus: t('meetings.fullPlanItem6_basic'),
        pro: t('meetings.fullPlanItem6_pro'),
        proPlus: t('meetings.fullPlanItem6_pro'),
        business: t('meetings.fullPlanItem6_biz'),
        businessPlus: t('meetings.fullPlanItem6_biz'),
        enterprise: t('meetings.fullPlanItem6_ent'),
      },
      {
        feature: t('knowledgeBase'),
        tooltip:
          t('troubleshootTechnicalIssuesAndAccessSupportResourcesBySearchingOurKnowledgeBaseArticles'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('zoomCommunity'),
        tooltip:
          t('aPlaceToFindSolutionsAskQuestionsAndCollaborateWithOtherZoomUsers'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('chatbot'),
        tooltip:
          t('getAnswersToQuestionsAndTroubleshootTechnicalIssuesByInteractingWithOurChatbot'),
        basic: true,
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('submitWebTickets'),
        basicPlus: true,
        pro: true,
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('liveChatSupport'),
        isString: true,
        basicPlus: true,
        pro: t('subscriptionPlansGreaterThan_dynamicUsdMonth', { amount: 10 }),
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('livePhoneSupport'),
        isString: true,
        tooltip: t('marketing.live_phone_support_tooltip'),
        basicPlus: true,
        pro: t('subscriptionPlansGreaterThan_200UsdMonth'),
        proPlus: true,
        business: true,
        businessPlus: true,
        enterprise: true,
      },
      {
        feature: t('meetWithAgentOnVideo'),
        isString: true,
        tooltip: t('marketing.meet_with_agent_on_video_tooltip'),
        business: t('subscriptionPlansGreaterThan_200UsdMonth'),
        businessPlus: t('subscriptionPlansGreaterThan_200UsdMonth'),
        enterprise: t('subscriptionPlansGreaterThan_200UsdMonth'),
      },
    ],
  };
};
